/*
 * Arbortech parallax quote banner behaviour.
 *
 * Faithful vanilla port of the GSAP ScrollTrigger parallax used in the Astro
 * source (src/scripts/arbortech-components.js, the [data-parallax="trigger"]
 * setup). GSAP/ScrollTrigger are not available in the theme, so this maps the
 * same scroll window onto a yPercent translate using requestAnimationFrame.
 *
 * Original behaviour:
 *   gsap.fromTo(target, { yPercent: start }, { yPercent: end, ease: 'none',
 *     scrollTrigger: { trigger, start: 'top bottom', end: 'bottom top',
 *     scrub: true } })
 *   start default -20 (data sets -40), end default 20 (data sets 40).
 *
 * Progress 0 → trigger top aligns with viewport bottom (entering).
 * Progress 1 → trigger bottom aligns with viewport top (leaving).
 * Translate = lerp(start, end, progress), applied as translateY(%).
 * Disabled when prefers-reduced-motion is set (matches the GSAP guard).
 */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var triggers = [];
  var ticking = false;

  function num(el, attr, fallback) {
    var raw = el.getAttribute(attr);
    if (raw === null || raw === '') return fallback;
    var parsed = parseFloat(raw);
    return isNaN(parsed) ? fallback : parsed;
  }

  function update() {
    ticking = false;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    for (var i = 0; i < triggers.length; i++) {
      var t = triggers[i];
      var rect = t.trigger.getBoundingClientRect();

      // 'top bottom' → 'bottom top'. Progress spans the full window from the
      // element's top entering the bottom of the viewport to its bottom
      // leaving the top of the viewport.
      var span = rect.height + viewportHeight;
      var progress = (viewportHeight - rect.top) / span;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      var yPercent = t.start + (t.end - t.start) * progress;
      t.target.style.transform = 'translateY(' + yPercent + '%)';
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  function disableAll() {
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].target.style.transform = '';
    }
  }

  function init() {
    triggers = [];
    var nodes = document.querySelectorAll('[data-parallax="trigger"]');
    Array.prototype.forEach.call(nodes, function (trigger) {
      var target = trigger.querySelector('[data-parallax="target"]') || trigger;
      triggers.push({
        trigger: trigger,
        target: target,
        start: num(trigger, 'data-parallax-start', 20),
        end: num(trigger, 'data-parallax-end', -20),
      });
    });

    if (prefersReduced.matches || triggers.length === 0) {
      disableAll();
      return;
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when sections are re-rendered in the Shopify theme editor.
  document.addEventListener('shopify:section:load', init);
  window.arbInitParallaxBanners = init;
})();

(function () {
  'use strict';

  var defaults = {
    direction: 'right',
    theme: '--arb-orange',
    scrollStart: 'top 90%',
    staggerStart: 'start',
    stagger: 100,
    barDuration: 0.6,
    barEase: 'power3.inOut',
  };

  var colorMap = {
    orange: '#e87722',
    white: '#ffffff',
  };

  var directionMap = {
    right: { prop: 'scaleX', origin: 'right center' },
    left: { prop: 'scaleX', origin: 'left center' },
    up: { prop: 'scaleY', origin: 'center top' },
    down: { prop: 'scaleY', origin: 'center bottom' },
  };

  function resolveColor(value) {
    if (colorMap[value]) return colorMap[value];
    if (value && value.indexOf('--') === 0) {
      return window.getComputedStyle(document.body).getPropertyValue(value).trim() || value;
    }
    return value || colorMap.orange;
  }

  function createBar(color, origin) {
    var bar = document.createElement('div');
    bar.className = 'highlight-marker-bar';
    bar.style.backgroundColor = color;
    bar.style.transformOrigin = origin;
    return bar;
  }

  function cleanupElement(el) {
    if (!el._highlightMarkerReveal) return;
    if (el._highlightMarkerReveal.timeline) el._highlightMarkerReveal.timeline.kill();
    if (el._highlightMarkerReveal.scrollTrigger) el._highlightMarkerReveal.scrollTrigger.kill();
    if (el._highlightMarkerReveal.originalHTML) el.innerHTML = el._highlightMarkerReveal.originalHTML;
    delete el._highlightMarkerReveal;
  }

  function revealAll() {
    // Disarm the CSS hide (see arb-parallax-quote.css: copy is visible by
    // default; .is-reveal-armed is added by the section's inline script only
    // when JS runs) and belt-and-braces force visibility inline.
    var banners = document.querySelectorAll('.arb-parallax-banner.is-reveal-armed');
    Array.prototype.forEach.call(banners, function (banner) {
      banner.classList.remove('is-reveal-armed');
    });

    var elements = document.querySelectorAll('.arb-parallax-banner [data-highlight-marker-reveal]');
    Array.prototype.forEach.call(elements, function (el) {
      el.style.visibility = 'visible';
      el.style.opacity = '1';
    });
  }

  function splitLines(el) {
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    var words = text.split(' ');
    var wordNodes = [];

    el.textContent = '';

    words.forEach(function (word, index) {
      var span = document.createElement('span');
      span.textContent = word + (index < words.length - 1 ? ' ' : '');
      el.appendChild(span);
      wordNodes.push(span);
    });

    var lineGroups = [];
    var activeTop = null;

    wordNodes.forEach(function (wordNode) {
      var top = Math.round(wordNode.getBoundingClientRect().top);
      if (activeTop === null || Math.abs(top - activeTop) > 2) {
        lineGroups.push([]);
        activeTop = top;
      }
      lineGroups[lineGroups.length - 1].push(wordNode.textContent);
    });

    el.textContent = '';

    return lineGroups.map(function (lineWords, index) {
      var line = document.createElement('span');
      line.className = 'highlight-marker-line';
      line.textContent = lineWords.join('').trim() + (index < lineGroups.length - 1 ? ' ' : '');
      el.appendChild(line);
      if (index < lineGroups.length - 1) el.appendChild(document.createElement('br'));
      return line;
    });
  }

  function initHighlightMarkerTextReveal() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var elements = document.querySelectorAll('.arb-parallax-banner [data-highlight-marker-reveal]');

    if (!elements.length) return;

    if (!gsap || !ScrollTrigger || reduceMotion) {
      revealAll();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    Array.prototype.forEach.call(elements, cleanupElement);

    Array.prototype.forEach.call(elements, function (el) {
      var direction = el.getAttribute('data-marker-direction') || defaults.direction;
      var theme = el.getAttribute('data-marker-theme') || defaults.theme;
      var scrollStart = el.getAttribute('data-marker-scroll-start') || defaults.scrollStart;
      var staggerStart = el.getAttribute('data-marker-stagger-start') || defaults.staggerStart;
      var staggerOffset = (parseFloat(el.getAttribute('data-marker-stagger')) || defaults.stagger) / 1000;
      var color = resolveColor(theme);
      var dirConfig = directionMap[direction] || directionMap.right;

      el._highlightMarkerReveal = {
        originalHTML: el.innerHTML,
      };

      // Heading + supporting copy stay in one flow so text-wrap: balance can
      // reflow (splitLines locks breaks via inline-block line spans + <br>).
      var lines;
      if (
        el.classList.contains('arb-parallax-banner__paragraph') ||
        el.classList.contains('arb-parallax-banner__heading')
      ) {
        var paragraphLine = document.createElement('span');
        paragraphLine.className = 'highlight-marker-line';
        paragraphLine.textContent = el.textContent.replace(/\s+/g, ' ').trim();
        el.textContent = '';
        el.appendChild(paragraphLine);
        lines = [paragraphLine];
      } else {
        lines = splitLines(el);
      }
      var tl = gsap.timeline({ paused: true });

      lines.forEach(function (line, index) {
        var staggerIndex = staggerStart === 'end' ? lines.length - 1 - index : index;
        var bar = createBar(color, dirConfig.origin);

        gsap.set(line, { position: 'relative', overflow: 'hidden' });
        line.appendChild(bar);

        tl.to(
          bar,
          {
            [dirConfig.prop]: 0,
            duration: defaults.barDuration,
            ease: defaults.barEase,
          },
          staggerIndex * staggerOffset
        );
      });

      gsap.set(el, { autoAlpha: 1 });
      el._highlightMarkerReveal.timeline = tl;
      el._highlightMarkerReveal.scrollTrigger = ScrollTrigger.create({
        trigger: el,
        start: scrollStart,
        once: true,
        onEnter: function () {
          tl.play();
        },
      });
    });
  }

  function initWhenReady() {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(initHighlightMarkerTextReveal);
    } else {
      initHighlightMarkerTextReveal();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }

  document.addEventListener('shopify:section:load', initWhenReady);
  window.arbInitHighlightMarkerTextReveal = initHighlightMarkerTextReveal;
})();
